<div class="max-w-7xl mx-auto">
  <h1 class="text-center font-medium text-gray-800">About CENTINELA Project</h1>

  <div class="border-2 border-gray-300 text-gray-200 p-4 rounded mt-6">
    <h2 class="mb-2 text-base text-gray-800">What is Centinela?</h2>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      Centinela is a constantly evolving project developed by students from the Faculty of Systems
      Engineering at the Escuela Politécnica Nacional as part of their final degree project. The
      primary purpose is to offer a platform that makes information about Ecuador's scientific
      production more visible through a retrieval system with three types of search and a dashboard
      section containing graphs of interest related to the country's scientific output.
    </p>
    <p class="text-lg text-gray-700 leading-relaxed text-sm">
      Additionally, Centinela is designed as a platform to support decision-making and academic
      interaction. Registered users can share relevant content, form research groups, and
      participate in a consensus workshop that facilitates the decision-making process to determine
      the optimal topic for the group to work on. In this way, Centinela aims to be an appealing
      tool that streamlines the search for scientific information and supports decision-making in
      the field of research.
    </p>
  </div>

  <h2 class="font-medium text-gray-800 mt-4">What can you find in Centinela?</h2>

  <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">
    <div class="relative w-full bg-gray-200 shadow-sm border-2 border-gray-300 rounded-lg">
      <div class="p-3 pb-0">
        <h2 class="text-xl text-center font-semibold text-gray-800 mb-0">
          CENTINELA as a Retrieval System
        </h2>
      </div>
      <div class="flex flex-col md:flex-row w-full">
        <!-- Contenedor de la imagen -->
        <div class="relative p-2.5 md:w-2/5 shrink-0 overflow-hidden flex justify-center">
          <img
            src="assets/svg-gettingstar/retrival.svg"
            alt="Sistema de Recuperación de Información"
            class="w-48 h-auto md:w-full md:h-full rounded-md md:rounded-lg object-contain"
          />
        </div>
        <!-- Contenedor del texto -->
        <div class="p-6">
          <p class="text-gray-700 leading-relaxed text-base">
            Find specific <span class="font-semibold">Authors</span>, discover experts on a topic
            through <span class="font-semibold">Relevant Authors</span>, and explore how Ecuadorian
            scientists contribute to a specific area with
            <span class="font-semibold">Relevant Articles</span>.
          </p>
        </div>
      </div>
    </div>

    <div class="relative w-full bg-white shadow-sm border-2 border-gray-300 rounded-lg">
      <div class="p-3 pb-0">
        <h2 class="text-xl text-center font-semibold text-gray-800 mb-0">
          CENTINELA as an Analytic Tool
        </h2>
      </div>
      <div class="flex flex-col md:flex-row-reverse w-full">
        <div class="relative p-2.5 md:w-2/5 shrink-0 overflow-hidden flex justify-center">
          <img
            src="assets/svg-gettingstar/dashboard.svg"
            alt="Analytic Tool"
            class="w-48 h-auto md:w-full md:h-full rounded-md md:rounded-lg object-contain"
          />
        </div>
        <div class="p-6">
          <p class="text-gray-700 leading-relaxed text-base">
            <span class="font-semibold">Visualize trends</span> to identify emerging research areas
            and <span class="font-semibold">explore demographic data</span> to observe the
            geographical distribution of scientific production.
          </p>
        </div>
      </div>
    </div>

    <div class="relative w-full shadow-sm border-2 border-gray-300 rounded-lg">
      <div class="p-3 pb-0">
        <h2 class="text-xl font-semibold text-center text-gray-800 mb-0">
          CENTINELA as an Academic Interaction Space
        </h2>
      </div>
      <div class="flex flex-col md:flex-row w-full">
        <div class="relative p-2.5 md:w-2/5 shrink-0 overflow-hidden flex justify-center">
          <img
            src="assets/svg-gettingstar/social.svg"
            alt="Academic Interaction Space"
            class="w-48 h-auto md:w-full md:h-full rounded-md md:rounded-lg object-contain"
          />
        </div>
        <div class="p-6">
          <p class="text-gray-700 leading-relaxed text-base">
            Beyond being a search tool, Centinela is a space for the academic community to
            <span class="font-semibold">make posts</span> share content of interest,
            <span class="font-semibold">connect with colleagues</span>, and explore other users'
            profiles.
          </p>
        </div>
      </div>
    </div>

    <div class="relative w-full bg-gray-200 shadow-sm border-2 border-gray-300 rounded-lg">
      <div class="p-3 pb-0">
        <h2 class="text-xl text-center font-semibold text-gray-800 mb-0">
          CENTINELA as Decision Support
        </h2>
      </div>
      <div class="flex flex-col md:flex-row-reverse w-full">
        <div class="relative p-2.5 md:w-2/5 shrink-0 overflow-hidden flex justify-center">
          <img
            src="assets/svg-gettingstar/consensus.svg"
            alt="Decision Support"
            class="w-48 h-auto md:w-full md:h-full rounded-md md:rounded-lg object-contain"
          />
        </div>
        <div class="p-6">
          <p class="text-gray-700 leading-relaxed text-base">
            <span class="font-semibold">Create teams</span> to participate in a
            <span class="font-semibold">consensus workshop</span> consisting of three phases to
            <span class="font-semibold">determine an optimal topic to work on together</span>.
            CENTINELA guides the process transparently.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Sección de Colaboradores -->
  <h2 id="collaborators" class="font-medium text-gray-800 text-center mb-0">Collaborators</h2>
  <div class="my-10 bg-gray-200 p-6">
    <div class="flex flex-col items-center">
      <p class="my-3 text-base font-semibold text-gray-800">PhD. Lorena Katherine Recalde Cerda</p>
      <p class="text-gray-600">Product Owner</p>
    </div>

    <!-- Cuadrícula de Colaboradores -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-4">
      <div class="flex flex-col items-center" *ngFor="let person of collaborators">
        <p class="my-3 text-base font-semibold text-gray-800">{{ person.name }}</p>
        <p class="text-gray-600 text-center">{{ person.role }}</p>
      </div>
    </div>
  </div>
</div>

<!-- <div class="flex items-center justify-center flex-col">
<p class="font-extrabold text-2xl">What is Centinela?</p>
<p class="">Centinela is a tool specifically designed to provide detailed academic information about Ecuadorian researchers and offers users three types of searches to access this information. Its main objective is to streamline and simplify the understanding of findings and results from relevant scientific research, allowing researchers to explore the relationships and occurrences of keywords used in these studies. </p>
<p>One of the key features of Centinela is its ability to assist researchers in finding experts in specific fields and learning about their areas of research. This is invaluable for establishing collaborations and finding specialized resources for academic projects.</p>
<p> To ensure the accuracy and relevance of the results presented, Centinela uses TF-IDF (Term Frequency-Inverse Document Frequency) models that have been trained with data obtained from Scopus, a widely recognized and trusted bibliographic database in the academic field. This ensures the quality and reliability of the information provided by ResNet.</p>
</div> -->
